/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import { Auth } from "@aws-amplify/auth";

export const STATE_START = "start";
export const STATE_UPLOADED = "uploaded";
export const STATE_UPLOAD_FAILED = "upload_failed";
export const STATE_CANCELLED = "protect_cancelled";
export const STATE_QUEUE = "protect_queue";
export const STATE_IN_PROGRESS = "protect_in_progress";
export const STATE_PENDING_SUPPORT = "protect_pending_support";
export const STATE_ERROR = "protect_error";
export const STATE_DONE = "protect_done";
export const STATE_EXPIRED = "expired";
export const STATE_NO_BUILDS = "no_builds";

const notify = (
  authState?: string,
  onAuthEvent?: any,
  onStateChange?: (state: string, data?: any) => void
) => {
  return {
    changeState: (state: string, data?: any): void => {
      if (!!onStateChange) {
        onStateChange(state, data);
      }
      if (!!onAuthEvent) {
        onAuthEvent(authState, { type: "stateChange", data: state });
      }
    },
  };
};

const usernameFromAuthData = (authData: any) => {
  if (!authData) {
    return "";
  }
  let username;
  if (typeof authData === "object") {
    username = authData.user ? authData.user.username : authData.username;
  } else {
    username = authData;
  }
  return username;
};

const emailValidator =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const urlValidator =
  /^((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const codeValidator = /^[0-9]{6}$/;

const passwordValidator =
  /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/;

const nameValidator =
  /^[a-zA-Z\xC0-\uFFFF-']{2,}(?: [a-zA-Z\xC0-\uFFFF-']+){0,3}$/;

const DEFAULT_TRIAL_DAYS_NUM =
  process.env.REACT_APP_DEFAULT_TRIAL_DAYS_NUM || "30";

const getUserDescription = (role: any) => {
  const roleDescriptions: { [key: string]: any } = {
    ADMIN: "Admin",
    SUPPORT: "Support",
    SUPPORT_RO: "Readonly Support",
    SUPPORT_ADMIN: "Support Admin",
  };
  return roleDescriptions[role] || "User";
};

const getRoleOptions = (userRole: string, getAll?: boolean) => {
  const roleOptions = [];

  if (userRole === "ADMIN" || getAll) {
    roleOptions.push(
      {
        key: "USER",
        value: "USER",
        text: getUserDescription("USER"),
      },
      {
        key: "ADMIN",
        value: "ADMIN",
        text: getUserDescription("ADMIN"),
      }
    );
  }

  if (userRole.includes("SUPPORT")) {
    roleOptions.push(
      {
        key: "SUPPORT",
        value: "SUPPORT",
        text: getUserDescription("SUPPORT"),
      },
      {
        key: "SUPPORT_RO",
        value: "SUPPORT_RO",
        text: getUserDescription("SUPPORT_RO"),
      },
      {
        key: "SUPPORT_ADMIN",
        value: "SUPPORT_ADMIN",
        text: getUserDescription("SUPPORT_ADMIN"),
      }
    );
  }

  return roleOptions;
};

const planInfo: { [key: string]: any } = {
  expired: {
    index: 0,
    id: "expired",
    color: "#dddddd",
    name: "Expired",
    price: "free",
    maxApps: "0",
  },
  free: {
    index: 1,
    id: "free",
    color: "#0676c5",
    name: "Free Trial",
    price: "free",
    maxApps: "2",
  },
  protectStarter: {
    index: 6,
    id: "protectStarter",
    color: "#e70072",
    name: "STARTER",
    price: "$349",
    maxApps: "2",
    maxVers: "6",
    description: "Easy entry with 2 apps included",
  },
  protectStarterSingleApp: {
    index: 7,
    id: "protectStarterSingleApp",
    color: "#e70072",
    name: "STARTER",
    price: "$179",
    maxApps: "1",
    maxVers: "6",
    description: "Easy entry with one app included",
  },
  protectStandard: {
    index: 8,
    id: "protectStandard",
    color: "#7209b7",
    name: "STANDARD",
    price: "$349",
    maxApps: "Unlimited",
    maxVers: "Unlimited",
    description: "App Shielding without limitations",
  },
  protectProfessional: {
    index: 9,
    id: "protectProfessional",
    color: "#0002b3",
    name: "PRO",
    price: "$649",
    maxApps: "Unlimited",
    maxVers: "Unlimited",
    description: "Scale with Enterprise Integration",
  },
  enhancedSupport: {
    index: 10,
    id: "enhancedSupport",
    color: "#0002b3",
    name: "Enterprise - Enhanced Support",
    price: "$649",
    maxApps: "Unlimited",
    maxVers: "Unlimited",
    description: "Enterprise - Enhanced Support",
  },
  monitoringSecurityOperationsCenter: {
    index: 11,
    id: "monitoringSecurityOperationsCenter",
    color: "#0002b3",
    name: "Security Operations Center",
    price: "",
    maxApps: "Unlimited",
    maxVers: "Unlimited",
    description: "Monitoring - Security Operations Center",
  },
  monitoringAppInstanceLookup: {
    index: 12,
    id: "monitoringAppInstanceLookup",
    color: "#0002b3",
    name: "App Instance Lookup",
    price: "",
    maxApps: "Unlimited",
    maxVers: "Unlimited",
    description: "Monitoring - App Instance Lookup",
  },
  monitoringUltimate: {
    index: 13,
    id: "monitoringUltimate",
    color: "#0002b3",
    name: "Ultimate",
    price: "",
    maxApps: "Unlimited",
    maxVers: "Unlimited",
    description: "Monitoring - Ultimate",
  },
  monitoringEndPointProtection: {
    index: 14,
    id: "monitoringEndPointProtection",
    color: "#0002b3",
    name: "End Point Protection",
    price: "",
    maxApps: "Unlimited",
    maxVers: "Unlimited",
    description: "Monitoring - End Point Protection",
  },
};

const getPlanInfo = (plan: any) => {
  return planInfo[plan] || planInfo["free"];
};

const getPlanId = (index: number) => {
  const plan = Object.values(planInfo).find(
    (planInfo: any) => planInfo.index === index
  );
  return plan.id || planInfo["free"].id;
};

const getHostInfo = () => {
  const hostname = window.location.hostname.replace(/^(www\.)/, "");
  const port = hostname.includes("local") ? ":3000" : "";
  const apsPort = hostname.includes("local") ? ":3100" : "";
  const monitorPort = hostname.includes("local") ? ":3400" : "";
  const protocol = `${window.location.protocol}//`;
  const hostInfo = { protocol, hostname, port, apsPort, monitorPort };
  return hostInfo;
};

const constructUrl = (subDomain = "") => {
  const hostInfo = getHostInfo();
  if (subDomain === "aps") {
    return `${hostInfo.protocol}aps.${hostInfo.hostname}${hostInfo.apsPort}`;
  }

  if (subDomain === "monitor") {
    return `${hostInfo.protocol}monitor.${hostInfo.hostname}${hostInfo.monitorPort}`;
  }
  return `${hostInfo.protocol}${hostInfo.hostname}${hostInfo.port}`;
};

const resetUrlPath = () => {
  if (window.location.pathname !== "/" || window.location.search) {
    window.history.pushState({}, document.title, "/");
  }
};

const isTest = () => {
  try {
    if (window.process.env.JEST_WORKER_ID) {
      return true;
    }
  } catch {
    // noop
  }
  return false;
};

const authCheckCallApi = async (apiCall: any, apiCallParams?: any) => {
  try {
    if (!isTest()) {
      await Auth.currentAuthenticatedUser();
    }
    apiCall(apiCallParams);
  } catch (e) {
    Auth.signOut().finally(
      () => (window.location.href = "/?sessionExpired=true")
    );
  }
};

const getUrlParam = (name: string) => {
  const queryString: URLSearchParams = new URLSearchParams(
    window.location.search
  );

  if (queryString.has(name)) {
    return queryString.get(name) as string;
  }

  return "";
};

const setUrlParam = (name: string, value: string) => {
  window.history.replaceState(
    {},
    document.title,
    `${window.location.pathname}?${name}=${encodeURIComponent(value)}`
  );
};

const resetUrlParams = () => {
  window.history.replaceState(
    {},
    document.title,
    `${window.location.pathname}`
  );
};

const setUrlPath = (path: string) => {
  window.history.replaceState({}, document.title, path);
};

const getUrlPath = () => {
  return window.location.pathname;
};

const refreshToken = async (onRefreshTokenComplete: any) => {
  try {
    const authenticatedUser = await Auth.currentAuthenticatedUser();
    const currentSession = await Auth.currentSession();

    authenticatedUser.refreshSession(
      currentSession.getRefreshToken(),
      onRefreshTokenComplete
    );
  } catch (e) {
    onRefreshTokenComplete();
  }
};

const isAdminRole = (role: string) => {
  return role.startsWith("ADMIN") || role.startsWith("SUPPORT");
};

const isSupportRole = (role: string) => {
  return role.startsWith("SUPPORT");
};

const isTrial = (plan: string) => {
  return plan === "free";
};

const isProPlan = (plan: string) => {
  return [
    "protectProfessional",
    "enhancedSupport",
    "monitoringSecurityOperationsCenter",
    "monitoringAppInstanceLookup",
    "monitoringUltimate",
    "monitoringEndPointProtection",
    undefined,
  ].includes(plan);
};

const isEnhancedPlan = (plan: string) => {
  return [
    "enhancedSupport",
    "monitoringSecurityOperationsCenter",
    "monitoringAppInstanceLookup",
    "monitoringUltimate",
    "monitoringEndPointProtection",
  ].includes(plan);
};

const isMonitoringPlan = (plan: string) => {
  return [
    "protectProfessional",
    "enhancedSupport",
    "monitoringSecurityOperationsCenter",
    "monitoringAppInstanceLookup",
    "monitoringUltimate",
    "monitoringEndPointProtection",
  ].includes(plan);
};

const isStandardPlan = (plan: string) => {
  return ["protectStandard"].includes(plan);
};

const parseGraphQlError = (error: any) => {
  if (!error) {
    return "";
  }

  let message;
  if (error.message) {
    message = error.message;
  }

  if (
    error.networkError &&
    error.networkError.result &&
    error.networkError.result.errors
  ) {
    if (error.networkError.result.errors.length > 0) {
      message = `${message}
                 ${error.networkError.result.errors[0].message}`;
    }
  }

  message = message.replace("GraphQL error: ", "\n");
  return message;
};

const getTrialExpires = (org: any) => {
  if (org.trialExpires) {
    return org.trialExpires;
  }

  const trialStarted = org.trialStarted || org.created;
  return trialStarted + 1000 * 3600 * 24 * parseInt(DEFAULT_TRIAL_DAYS_NUM);
};

const DateFormatLong: any = {
  weekday: "short",
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
};

const getPlatformUrl = (path?: "register") => {
  return `https://portal.platform.verimatrix${
    process.env.NODE_ENV === "development" ? "uat" : ""
  }cloud.net${path === "register" ? "/#/register/" : ""}`;
};

export {
  notify,
  usernameFromAuthData,
  emailValidator,
  codeValidator,
  passwordValidator,
  nameValidator,
  getRoleOptions,
  getUserDescription,
  getPlanInfo,
  getPlanId,
  constructUrl,
  authCheckCallApi,
  resetUrlPath,
  getUrlParam,
  setUrlParam,
  resetUrlParams,
  setUrlPath,
  getUrlPath,
  refreshToken,
  isAdminRole,
  isSupportRole,
  isProPlan,
  isEnhancedPlan,
  isStandardPlan,
  isMonitoringPlan,
  parseGraphQlError,
  DEFAULT_TRIAL_DAYS_NUM,
  getTrialExpires,
  isTrial,
  DateFormatLong,
  urlValidator,
  getPlatformUrl,
};
